import type { Auth, User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { dayText } from "./accounts";
import { UNIT_COLUMNS } from "./columns";
import { go, useCrumb, useTitle } from "./hooks";
import { TabHead } from "./Menu";
import { type Saved, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { nextInOrder, PLACE_TEXT, PLACES, rangeSerials, serialLengthText, sizeText, STATUS_TEXT, whereText } from "./units";
import { personName } from "./requests";
import { type Failure, failureOf, Problem, Status } from "./UserPages";
import { Skeleton } from "./Skeleton";

const day = (iso: string | null | undefined) => (iso ? dayText(iso.slice(0, 10), true) : "—");

/** Spec F-INV-2: each container unit by its serial number, with its status and place (rule A-7). */
export function UnitList({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [found, setFound] = useState<api.UnitList | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [adding, setAdding] = useState(false);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [sort, setSort] = useSort("sort", UNIT_COLUMNS);
  useTitle(`Containers · Units · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then(api.units)
      .then((list) => live && setFound(list))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt, canView]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="inventory-hidden">
        <TabHead />
        <p>Your role does not show the container units.</p>
      </main>
    );
  }
  const list = typeof found === "object" ? found : null;
  return (
    <main className="inventory-page wide" data-testid="inventory-page">
      <TabHead />
      <p className="sub">Each container by its serial number: where it is now, and each move.</p>
      {list?.can_add && !adding && (
        <div className="tabs">
          <button type="button" className="primary" data-testid="unit-add" onClick={() => setAdding(true)}>
            Add units
          </button>
        </div>
      )}
      {list && adding && (
        <AddForm
          list={list}
          auth={auth}
          user={user}
          onCancel={() => setAdding(false)}
          onAdded={(count) => {
            setAdding(false);
            setSaved({ ok: true, text: count === 1 ? "Added 1 unit to the yard." : `Added ${count} units to the yard.` });
            setAttempt((n) => n + 1);
          }}
        />
      )}
      <Status saved={saved} auth={auth} testId="unit-status-line" />
      {found === undefined ? (
        <Skeleton shape="table" columns={4} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : !found.units.length ? (
        <p data-testid="inventory-empty">No units yet.</p>
      ) : (
        <>
          <SortSelect id="units-select" columns={UNIT_COLUMNS} sort={sort} onSort={setSort} fallback="Serial" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Container units">
            <table className="list" data-testid="unit-table" data-cols="units-select">
              <thead>
                <tr>
                  {UNIT_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(found.units, UNIT_COLUMNS, sort).map((unit) => (
                  <tr key={unit.id} className={unit.status === "retired" ? "muted" : undefined} data-testid="unit-row" data-serial={unit.serial} data-status={unit.status}>
                    <th scope="row">
                      <a href={`${base}/inventory/${unit.id}`} onClick={go}>
                        {unit.serial}
                      </a>
                    </th>
                    <td className="num" data-label="Size">{sizeText(unit.size_gal)}</td>
                    <td data-label="Status">
                      <span className="chip-flag">{STATUS_TEXT[unit.status]}</span>
                    </td>
                    <td data-label="Where">{whereText(unit)}</td>
                    <td data-label="Last move">{day(unit.last_move_at)}</td>
                    <td data-label="Note">{unit.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}

function AddForm({ list, auth, user, onCancel, onAdded }: { list: api.UnitList; auth: Auth; user: User; onCancel: () => void; onAdded: (count: number) => void }) {
  const [ranged, setRanged] = useState(false);
  const [serial, setSerial] = useState("");
  const [prefix, setPrefix] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [size, setSize] = useState(String(list.sizes[0] ?? ""));
  const [hub, setHub] = useState("");
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const range = ranged ? rangeSerials(prefix, first, last) : null;
  const tooLong = ranged ? null : serialLengthText(serial);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (tooLong) return;
    if (typeof range === "string") return setSaved({ ok: false, text: range });
    if (!ranged && !serial.trim()) return setSaved({ ok: false, text: "Give the serial number." });
    setSaving(true);
    setSaved(null);
    try {
      const where = { size_gal: Number(size), hub_id: hub || null, note: note.trim() };
      const entry: api.UnitEntry = ranged ? { ...where, prefix, first, last } : { ...where, serial: serial.trim() };
      onAdded((await api.addUnits(await user.getIdToken(), entry)).added);
    } catch (e) {
      setSaved(saveFailure(e, "The units"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="unit-form" aria-labelledby="unit-form-title">
      <h2 id="unit-form-title">Add units to the yard</h2>
      <fieldset>
        <legend>How many</legend>
        <label className="check">
          <input type="radio" name="unit-count" checked={!ranged} onChange={() => setRanged(false)} /> One unit
        </label>
        <label className="check">
          <input type="radio" name="unit-count" data-testid="unit-ranged" checked={ranged} onChange={() => setRanged(true)} /> A range of numbers
        </label>
      </fieldset>
      {ranged ? (
        <>
          <label htmlFor="unit-prefix">Text before the number</label>
          <input id="unit-prefix" data-testid="unit-prefix" value={prefix} maxLength={30} autoComplete="off" onChange={(e) => setPrefix(e.target.value)} />
          <label htmlFor="unit-first">First number</label>
          <input id="unit-first" data-testid="unit-first" inputMode="numeric" value={first} maxLength={8} onChange={(e) => setFirst(e.target.value)} />
          <label htmlFor="unit-last">Last number</label>
          <input id="unit-last" data-testid="unit-last" inputMode="numeric" value={last} maxLength={8} onChange={(e) => setLast(e.target.value)} />
          <p className="sub" data-testid="unit-range-text">
            {Array.isArray(range) && range.length ? `${range.length} units: ${range[0]} to ${range[range.length - 1]}` : "The first number sets the count of digits, for example 001."}
          </p>
        </>
      ) : (
        <>
          <label htmlFor="unit-serial">Serial number</label>
          <input
            id="unit-serial"
            data-testid="unit-serial"
            value={serial}
            autoComplete="off"
            aria-invalid={tooLong !== null}
            aria-describedby={tooLong ? "unit-serial-long" : undefined}
            onChange={(e) => setSerial(e.target.value)}
          />
          {tooLong && (
            <p className="error" id="unit-serial-long" data-testid="unit-serial-long">
              {tooLong}
            </p>
          )}
        </>
      )}
      <label htmlFor="unit-size">Size</label>
      <select id="unit-size" data-testid="unit-size" value={size} onChange={(e) => setSize(e.target.value)}>
        {list.sizes.map((value) => (
          <option key={value} value={value}>
            {sizeText(value)}
          </option>
        ))}
      </select>
      {list.hubs.length > 0 && (
        <>
          <label htmlFor="unit-hub">Hub</label>
          <select id="unit-hub" value={hub} onChange={(e) => setHub(e.target.value)}>
            <option value="">No hub</option>
            {list.hubs.map((value) => (
              <option key={value.id} value={value.id}>
                {value.name}
              </option>
            ))}
          </select>
        </>
      )}
      <label htmlFor="unit-note">Note (optional)</label>
      <input id="unit-note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      <div className="actions">
        <button type="submit" className="primary" data-testid="unit-save" disabled={saving || tooLong !== null}>
          Add
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <Status saved={saved} auth={auth} testId="unit-form-status" />
    </form>
  );
}

/** One unit: where it is, a move to a new status, and each move, newest first. */
export function UnitPage({ id, base, org, auth, user }: { id: string; base: string; org: api.Bootstrap; auth: Auth; user: User }) {
  const [found, setFound] = useState<api.UnitDetail | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [moved, setMoved] = useState(0);
  const statusLine = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (moved > 0) statusLine.current?.focus();
  }, [moved]);
  const detail = typeof found === "object" ? found : null;
  useTitle(`${detail ? detail.unit.serial : "Unit"} · ${org.name}`);
  useCrumb(detail ? detail.unit.serial : null);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.unit(token, id))
      .then((next) => live && setFound(next))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, id, attempt]);
  if (found === "hidden") {
    return (
      <main data-testid="unit-missing">
        <h1 tabIndex={-1}>Unit not found</h1>
        <p>
          No unit at this link is visible to you.{" "}
          <a href={`${base}/inventory`} onClick={go}>
            Go to the container units
          </a>
        </p>
      </main>
    );
  }
  if (!detail) {
    return <main>{typeof found === "string" ? <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} /> : <Skeleton shape="detail" />}</main>;
  }
  const { unit, moves } = detail;
  return (
    <main className="unit-page" data-testid="unit-page">
      <p>
        <a href={`${base}/inventory`} onClick={go}>
          ← All container units
        </a>
      </p>
      <h1 tabIndex={-1}>{unit.serial}</h1>
      <dl className="facts">
        <dt>Size</dt>
        <dd>{sizeText(unit.size_gal)}</dd>
        <dt>Status</dt>
        <dd data-testid="unit-status">
          <span className="chip-flag">{STATUS_TEXT[unit.status]}</span>
        </dd>
        <dt>Where</dt>
        <dd data-testid="unit-where">
          {unit.place === "account" && unit.account_id ? (
            <a href={`${base}/accounts/${unit.account_id}`} onClick={go}>
              {whereText(unit)}
            </a>
          ) : (
            whereText(unit)
          )}
        </dd>
        {unit.note && (
          <>
            <dt>Note</dt>
            <dd>{unit.note}</dd>
          </>
        )}
      </dl>
      {detail.can_move && detail.next.length > 0 && (
        <MoveForm
          key={`${unit.status}-${moves.length}`}
          detail={detail}
          auth={auth}
          user={user}
          onSaved={setSaved}
          onMoved={(next) => {
            setFound(next);
            setSaved({ ok: true, text: `Saved: ${STATUS_TEXT[next.unit.status]}, ${whereText(next.unit)}.` });
            setMoved((n) => n + 1);
          }}
        />
      )}
      {detail.can_move && detail.next.length === 0 && <p className="sub">A retired unit cannot move.</p>}
      <Status saved={saved} auth={auth} testId="unit-move-status" focusRef={statusLine} />
      <h2>Moves</h2>
      <ol className="unit-moves" data-testid="unit-moves">
        {moves.map((move, index) => (
          <li key={`${move.at}-${index}`} data-testid="unit-move" data-to={move.to_status}>
            {day(move.at)}: {move.from_status ? `${STATUS_TEXT[move.from_status]} (${move.from_where}) → ` : "Added: "}
            {STATUS_TEXT[move.to_status]} ({move.to_where}){move.work_order_id ? ", by a work order" : ""}
            {move.moved_by ? `, ${personName(move.moved_by)}` : ""}
            {move.note ? `. ${move.note}` : ""}
          </li>
        ))}
      </ol>
    </main>
  );
}

function MoveForm({
  detail,
  user,
  onSaved,
  onMoved,
}: {
  detail: api.UnitDetail;
  auth: Auth;
  user: User;
  onSaved: (saved: Saved | null) => void;
  onMoved: (next: api.UnitDetail) => void;
}) {
  const order = nextInOrder(detail.next);
  const [status, setStatus] = useState<api.UnitStatus>(order[0]);
  const [asking, setAsking] = useState(false);
  const question = useRef<HTMLParagraphElement>(null);
  const save = useRef<HTMLButtonElement>(null);
  const backToSave = useRef(false);
  useEffect(() => {
    if (asking) question.current?.focus();
    else if (backToSave.current) {
      backToSave.current = false;
      save.current?.focus();
    }
  }, [asking]);
  const places = PLACES[status];
  const [place, setPlace] = useState<api.UnitPlace>(places[0]);
  const [hub, setHub] = useState("");
  const [truck, setTruck] = useState("");
  const [account, setAccount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const where = places.includes(place) ? place : places[0];
  const pick = (next: api.UnitStatus) => {
    setAsking(false);
    setStatus(next);
    setPlace(PLACES[next][0]);
  };
  const keep = () => {
    backToSave.current = true;
    setAsking(false);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (status === "retired" && !asking) return setAsking(true);
    backToSave.current = asking;
    setAsking(false);
    if (where === "truck" && !truck.trim()) return onSaved({ ok: false, text: "Give the truck." });
    if (where === "account" && !account.trim()) return onSaved({ ok: false, text: "Give the account number." });
    setSaving(true);
    onSaved(null);
    try {
      const entry: api.UnitMoveEntry = { status, place: where, note: note.trim() };
      if (where === "hub") entry.hub_id = hub || null;
      if (where === "truck") entry.truck = truck.trim();
      if (where === "account") entry.account_number = account.trim();
      onMoved(await api.moveUnit(await user.getIdToken(), detail.unit.id, entry));
    } catch (e) {
      onSaved(saveFailure(e, "The move"));
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="unit-move-form" aria-labelledby="unit-move-title">
      <h2 id="unit-move-title">Move the unit</h2>
      <label htmlFor="unit-next">New status</label>
      <select id="unit-next" data-testid="unit-next" value={status} onChange={(e) => pick(e.target.value as api.UnitStatus)}>
        {order.map((value) => (
          <option key={value} value={value}>
            {STATUS_TEXT[value]}
          </option>
        ))}
      </select>
      {places.length > 1 && (
        <>
          <label htmlFor="unit-place">Place</label>
          <select id="unit-place" value={where} onChange={(e) => setPlace(e.target.value as api.UnitPlace)}>
            {places.map((value) => (
              <option key={value} value={value}>
                {PLACE_TEXT[value]}
              </option>
            ))}
          </select>
        </>
      )}
      {where === "hub" && detail.hubs.length > 0 && (
        <>
          <label htmlFor="unit-move-hub">Hub</label>
          <select id="unit-move-hub" value={hub} onChange={(e) => setHub(e.target.value)}>
            <option value="">No hub</option>
            {detail.hubs.map((value) => (
              <option key={value.id} value={value.id}>
                {value.name}
              </option>
            ))}
          </select>
        </>
      )}
      {where === "truck" && (
        <>
          <label htmlFor="unit-truck">Truck</label>
          <input id="unit-truck" data-testid="unit-truck" value={truck} maxLength={40} autoComplete="off" onChange={(e) => setTruck(e.target.value)} />
        </>
      )}
      {where === "account" && (
        <>
          <label htmlFor="unit-account">Account number</label>
          <input id="unit-account" data-testid="unit-account" value={account} maxLength={40} autoComplete="off" onChange={(e) => setAccount(e.target.value)} />
        </>
      )}
      <label htmlFor="unit-move-note">Note (optional)</label>
      <input id="unit-move-note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      {asking ? (
        <div className="confirm" data-testid="unit-retire-ask" onKeyDown={(e) => e.key === "Escape" && keep()}>
          <p tabIndex={-1} ref={question}>
            Retire {detail.unit.serial}? You cannot move it after this.
          </p>
          <div className="actions">
            <button type="submit" className="danger" data-testid="unit-retire-confirm" disabled={saving}>
              Retire
            </button>
            <button type="button" data-testid="unit-retire-keep" onClick={keep}>
              Keep it
            </button>
          </div>
        </div>
      ) : (
        <div className="actions">
          <button type="submit" className="primary" data-testid="unit-move-save" disabled={saving} ref={save}>
            Save the move
          </button>
        </div>
      )}
    </form>
  );
}
